/**
 * Production-safe Logger
 * Only logs to console in development environments.
 * Errors are always logged and sent to Sentry in production.
 */

import { captureError, captureMessage } from './sentry.js';

const isDev = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('netlify.app') // Preview deploys
);

/**
 * Logger object with environment-aware logging
 */
export const logger = {
    /**
     * Log general information (dev only)
     */
    log: (...args) => {
        if (isDev) console.log(...args);
    },

    /**
     * Log warnings (dev only)
     */
    warn: (...args) => {
        if (isDev) console.warn(...args);
    },

    /**
     * Log a warning and record it in Sentry at warning level.
     *
     * For failures that are expected in the wild and need no code change
     * (client offline, request blocked by an extension) but should still be
     * countable. Keeps them out of the error stream so errors stay actionable.
     * @param {string} message - Stable message; Sentry groups on it
     * @param {Object} context - Optional { tags, extra }
     */
    warnRemote: (message, context = {}) => {
        if (isDev) console.warn(message, context.extra || '');
        captureMessage(message, 'warning', context);
    },

    /**
     * Log errors (always - needed for debugging production issues)
     * Also sends to Sentry for aggregation
     */
    error: (...args) => {
        console.error(...args);

        // Send to Sentry for production error tracking
        const firstArg = args[0];
        if (firstArg instanceof Error) {
            captureError(firstArg, {
                extra: { additionalArgs: args.slice(1) }
            });
        } else if (typeof firstArg === 'string') {
            // Create an error from the message for better stack traces
            const error = new Error(firstArg);
            captureError(error, {
                extra: { additionalArgs: args.slice(1) }
            });
        }
    },

    /**
     * Log debug information (dev only)
     */
    debug: (...args) => {
        if (isDev) console.debug(...args);
    },

    /**
     * Log with a group (dev only)
     */
    group: (label) => {
        if (isDev) console.group(label);
    },

    /**
     * End a console group (dev only)
     */
    groupEnd: () => {
        if (isDev) console.groupEnd();
    }
};
